import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import Link from '@/components/Link';
import { Button } from '@forma/forma-ui-kit';
import { useTheme } from '@forma/forma-ui-theme';
import Logo from '@/components/Logo';
import analytics from '@/helpers/analytics';
import SideMenuItems from './SideMenuItems';
import BurgerIcon from './BurgerIcon';

import { headerAtom, mainCompanyDataAtom, topPanelAtom } from '@/store/common';

import styles from './side-menu.module.css';

const lkUrl = process.env.LK_URL;
const demoUrl = process.env.DEMO_URL;

const SideMenu = () => {
  const { t } = useTranslation();
  const { apps } = useAtomValue(headerAtom);
  const { phone, whatsapp, telegram } = useAtomValue(mainCompanyDataAtom);
  const { left, right } = useAtomValue(topPanelAtom);
  const { lang } = useTheme();
  const [isMenuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (isMenuOpen) document.body.classList.add('menu-open');
    else document.body.classList.remove('menu-open');

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  return (
    <>
      <Button
        className={styles.menuButton}
        viewStyle="text"
        onClick={() => setMenuOpen(true)}
        icon={<BurgerIcon />}
      />
      <div className={classNames(styles.overlay, isMenuOpen && styles.open)} onClick={() => setMenuOpen(false)} />
      <div className={classNames(styles.menu, isMenuOpen && styles.open)}>
        <div className={styles.head}>
          <Logo onClick={() => setMenuOpen(false)} />
          <ReactSVG src="/icons/close.svg" className={styles.closeButton} onClick={() => setMenuOpen(false)} />
        </div>
        <div className={styles.content}>
          <SideMenuItems onClickItem={() => setMenuOpen(false)} />

          <div className={styles.links}>
            {left.map(({ name, href, icon }) => (
              <Link
                href={href}
                className={styles.link}
                target={href.startsWith('http') ? '_blank' : undefined}
                onClick={() => setMenuOpen(false)}
                key={name}
              >
                {icon && <ReactSVG className={styles.icon} src={icon} wrapper="span" />}
                {name}
              </Link>
            ))}
            {right.map(({ name, href, icon }) => (
              <Link
                href={href}
                className={styles.link}
                target={href.startsWith('http') ? '_blank' : undefined}
                onClick={() => setMenuOpen(false)}
                key={name}
              >
                {icon && <ReactSVG className={styles.icon} src={icon} wrapper="span" />}
                {name}
              </Link>
            ))}
          </div>

          <div className={styles.buttonsRow}>
            {whatsapp && (
              <Button
                as={Link}
                href={whatsapp.href}
                className={styles.squareButton}
                viewStyle="secondary"
                target="_blank"
                iconClassName={styles.squareButtonIcon}
                icon={whatsapp.icon && <ReactSVG src={whatsapp.icon} wrapper="span" />}
              />
            )}
            {telegram && (
              <Button
                as={Link}
                href={telegram.href}
                className={styles.squareButton}
                viewStyle="secondary"
                target="_blank"
                iconClassName={styles.squareButtonIcon}
                icon={telegram.icon && <ReactSVG src={telegram.icon} wrapper="span" />}
              />
            )}
            {phone && (
              <Link
                href={phone.href}
                className={styles.phoneLink}
              >
                {phone.name}
              </Link>
            )}
          </div>
          <div className={styles.buttonsRow}>
            {apps.map(({ name, imageWhite, href }) => (
              <Link className={styles.appLink} href={href} key={name}>
                <img className={styles.appLinkIcon} src={imageWhite} height={34} alt={name} />
              </Link>
            ))}
          </div>
        </div>
        <div className={styles.buttonsBottom}>
          <Button
            as={Link}
            href={`${lkUrl}/register?lng=${lang}`}
            className={classNames(styles.button, styles.register)}
            viewStyle="primary"
            size="m"
            onClick={() => {
              analytics.trackYandex('main_free_start_header');
              setMenuOpen(false);
            }}
            fullWidth
          >
            {t('start_free')}
          </Button>
          <Button
            as="a"
            href={demoUrl || ''}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(styles.button, styles.signIn)}
            viewStyle="secondary"
            size="m"
            onClick={() => {
              analytics.trackYandex('main_demo_header');
              setMenuOpen(false);
            }}
            fullWidth
          >
            {t('request_demo')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default SideMenu;
